// itemRedirects — the old staff URLs, kept working (§9, T20).
//
//   "Old URLs are in people's inboxes and Linear. /threads/:threadId and
//    /agreements redirects ship in the same web deploy as the deletions."
//
// Four routes are being retired and every one of them has been pasted into a
// Linear comment, a Slack thread or an email at some point. None of them may
// 404, and none of them may land somewhere that makes the operator hunt: a
// thread link goes to the item it was about, a /docs link with a document open
// goes to that document, a /docs link that was a FILTER becomes the same filter
// on the new list.
//
// Consumer links are untouched — signers get `/sign/:uuid` and brokers
// `/l/:token`. Only staff URLs move.
//
// Two things about how this is written:
//
//   • The destination is computed by a PURE function (`threadRedirectTarget`,
//     `docsRedirectTarget`) and the component is a two-line wrapper. The
//     redirect table is then a unit test rather than a browser exercise.
//   • Each component also emits a hidden `<span data-redirect-to>`. `<Navigate>`
//     renders nothing and navigates from an effect, which server-side rendering
//     does not run — so without the marker "did this redirect go to the right
//     place?" is not answerable by a test at all, only by clicking. It costs one
//     empty span and it is also what you read in devtools when a redirect
//     misfires.

import { useEffect } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from '@/components/Toast';
import {
  SENT_ITEMS_PATH,
  itemIdForDocParams,
  itemKindForDocKind,
  redirectPathForThreadId,
} from '@/lib/sentItemIds';
import { sentItemsHrefForPhase } from '@/components/sentItems/phaseFilters';
import { TYPE_GROUP_BY_KIND } from '@/lib/sentItemsClient';

/** Shown when a link resolved to nothing in particular. Not an error: the
 *  thread it named is gone as a concept, and the list is where its item is. */
const MOVED_NOTE = "That link's item moved; here is the list.";

// ---------------------------------------------------------------------------
// Targets (pure)
// ---------------------------------------------------------------------------

/** `/threads/:threadId` → where it goes now. */
export function threadRedirectTarget(threadId: string | undefined): string {
  if (!threadId) return SENT_ITEMS_PATH;
  return redirectPathForThreadId(threadId);
}

/**
 * `/docs…` → where it goes now.
 *
 * An OPEN document wins over the list filter: `/docs?kind=lease&sk=lease&sid=g`
 * is a link to one lease that happened to be found under a lease filter, and it
 * is the lease the sender meant. With nothing open, the link was a VIEW — the
 * filters carry across so the recipient sees the same set:
 *
 *   kind  → `types`, through the row kind (a `/docs` kind is a document kind;
 *           three of them are one row — the intake sections), then §6.2's chip
 *           group. `intake_animals` therefore becomes `types=intake`.
 *   state → carried verbatim; Sent Items uses the same `ViewDocState`
 *           vocabulary (§5.2).
 *   q     → carried verbatim. Not named in §9, but silently dropping the
 *           operator's search would make the redirect lose the only thing they
 *           typed.
 */
export function docsRedirectTarget(search: URLSearchParams): string {
  const sk = search.get('sk');
  const sid = search.get('sid');
  const kind = search.get('kind') ?? '';

  const itemId = itemIdForDocParams(kind, sk, sid);
  if (itemId) return `/items/${encodeURIComponent(itemId)}`;

  const next = new URLSearchParams();
  const rowKind = itemKindForDocKind(kind);
  if (rowKind) next.set('types', TYPE_GROUP_BY_KIND[rowKind]);
  const state = search.get('state');
  if (state) next.set('state', state);
  const q = search.get('q');
  if (q) next.set('q', q);

  const qs = next.toString();
  return qs ? `${SENT_ITEMS_PATH}?${qs}` : SENT_ITEMS_PATH;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function Redirecting({ to }: { to: string }): React.JSX.Element {
  return (
    <>
      <span hidden data-redirect-to={to} />
      <Navigate to={to} replace />
    </>
  );
}

/** `/threads/:threadId` → `/items/:itemId`, else the list with a note. */
export function ThreadRedirect(): React.JSX.Element {
  const { threadId } = useParams();
  const to = threadRedirectTarget(threadId);
  // Only when the link lost its specific destination. A thread that resolved to
  // its item, or a rental verification that resolved to its group's search, has
  // gone exactly where it meant to and needs no explanation.
  const lost = to === SENT_ITEMS_PATH;
  useEffect(() => {
    if (!lost) return;
    // Deferred by a macrotask ON PURPOSE, and deliberately NOT cleaned up.
    // `<Toaster/>` lives inside AppShell and AppShell remounts across this
    // navigation, so a toast emitted in this commit is pushed to the listener
    // that is about to be torn down and is never seen. By the next macrotask
    // the list's shell is mounted and it lands there. Clearing the timeout on
    // unmount would cancel it, because unmounting is exactly what happens.
    setTimeout(() => toast.info(MOVED_NOTE), 0);
  }, [lost]);
  return <Redirecting to={to} />;
}

/** `/docs` (and `/docs?sk=&sid=`) → the item, or the same view on the list. */
export function DocsRedirect(): React.JSX.Element {
  const [search] = useSearchParams();
  return <Redirecting to={docsRedirectTarget(search)} />;
}

/**
 * `/agreements?type=&phase=` → the same narrowing on the new list.
 *
 * §9 only says "→ /sent-items", and a bare redirect would satisfy it. But
 * these URLs are drill-downs — "the 3 leases waiting on WHP" — and a redirect
 * that drops the narrowing turns each one into "everything ever sent" with no
 * sign that anything was lost.
 *
 * ALL SEVEN phases now translate. They used to be three: `out`, `countersign`
 * and `blocked` had §6.2 equivalents and `preparing` / `ready` / `done` /
 * `closed` were dropped, because nothing on the list could express "generated
 * and never sent" or "cancelled, voided or declined". The `step` filter (an
 * exact `ItemLifecycle.step` match) can, so the whole table lives in
 * `phaseFilters.ts` and BOTH this redirect and Overview's own links read it —
 * Overview links straight to `/sent-items` now, which is why this is a
 * compatibility shim rather than the mechanism.
 *
 * `lens=` still goes: §6.2 retires the Threads/Submissions lens, because
 * versions live inside the row now.
 */
export function agreementsRedirectTarget(search: URLSearchParams): string {
  return sentItemsHrefForPhase(search.get('type'), search.get('phase'), {
    // Not named in §9, but silently dropping the operator's search would make
    // the redirect lose the only thing they typed.
    q: search.get('q'),
  });
}

/** `/agreements` → the list that replaced it. */
export function AgreementsRedirect(): React.JSX.Element {
  const [search] = useSearchParams();
  return <Redirecting to={agreementsRedirectTarget(search)} />;
}
